import { useContext, useMemo } from 'react'
import { Dialog } from 'primereact/dialog'
import { SelectButton } from 'primereact/selectbutton'
import { Button } from 'primereact/button'
import { classNames } from 'primereact/utils'

// Custom components
import { LoaderMedium } from '../Loaders'
import StandardPageHeader from '../StandardPageHeader/StandardPageHeader'
import AppointmentFilters from './AppointmentFilters'
import AppointmentsCalendarView from './AppointmentsCalendarView'
import AppointmentsListView from './AppointmentsListView'
import { UserContext } from "../../context/userContext"

// Styles
import myStyles from "../../styles/appointments/Appointments.module.css"

export default function AppointmentsDialog({
  fetchAppointments,
  showApptsDialog,
  setShowApptsDialog,
  initializationCompleted,
  isCalendarView,
  setIsCalendarView,
  filterServices,
  setFilterServices,
  services,
  loadingServices,
  loadingAppointments,
  loadingAppointmentsSmall,
  calSelectedDate,
  setCalSelectedDate,
  handleDateRangeChanged,
  filterDateRange,
  filteredAppointments,
  openRequestModal,
  openEditModal,
  openDeleteConfirmation,
  openCancelConfirmation,
}) {
  const userCtx = useContext(UserContext)
  const viewModes = [ // Options for switching between calendar and list views.
    { name: 'Calendar', code: 'calendar', icon: 'pi pi-calendar' },
    { name: 'List', code: 'list', icon: 'pi pi-list' },
  ];

  const selectButtonTemplate = (item) => {
    return (
      <span className={myStyles.viewSelectButtonTemplate}>
        <i className={classNames(item.icon, "mr-3", myStyles.selectButtonTemplateIcon)} />
        <span className={myStyles.viewSelectButtonText}>{item.name}</span>
      </span>
    )
  }

  const orgNameMap = useMemo(() =>
    (userCtx.participant_organizations || []).reduce((acc, org) => {
      acc[org.organization_id] = org.organization_name;
      if (org.organization_alias) {
        acc[org.organization_alias] = org.organization_name;
      }
      return acc;
    }, {}),
    [userCtx.participant_organizations]);

  const getOrgNameFromMap = (id) => orgNameMap[id] || "Unknown Organization";

  return (
    <Dialog
      header={() => (
        <StandardPageHeader
          title="Appointments"
          refreshData={() => { fetchAppointments(true) }}
          overrideClassNames={"pageHeaderCustom"}
        />
      )}
      visible={showApptsDialog}
      onHide={() => {
        setShowApptsDialog(false)
        initializationCompleted.current = false // Reset initialization flag when dialog is closed.
      }}
      className={classNames("custom_dialog", myStyles.mainDialog)}
    >
      <div className={myStyles.contentMain}>

        {/* Header row containing view mode selector, filters, and request appointment button */}
        <div className={myStyles.headerRow}>
          <SelectButton
            className={myStyles.viewSelectButton}
            value={isCalendarView ? viewModes[0] : viewModes[1]}
            onChange={(e) => {
              setIsCalendarView(e.value?.code === "calendar")
            }}
            options={viewModes}
            optionLabel="name"
            itemTemplate={selectButtonTemplate}
            disabled={loadingServices || loadingAppointments || loadingAppointmentsSmall} // Disable button if services or appointments are loading.
          />

          <AppointmentFilters
            filterValues={filterServices}
            onChangeFilterValues={(value) => {
              setFilterServices(value)
            }}
            services={services?.flat_unique || []}
            loading={loadingServices || loadingAppointments || loadingAppointmentsSmall}
          />

          <Button
            className={classNames(myStyles.roundedDialogButton, myStyles.requestButton)}
            onClick={openRequestModal}
            disabled={loadingServices || loadingAppointments || loadingAppointmentsSmall} // Disable button if services or appointments are loading.
          >
            Request Appointment
          </Button>
        </div>

        {/* Show loader if data is loading. */}
        {(loadingServices || loadingAppointments) && (
          <LoaderMedium />
        )}

        {loadingAppointmentsSmall && (
          <div className={myStyles.loadingSmallContainer}>
            <LoaderMedium />
            <span>Loading...</span>
          </div>
        )}

        {(!loadingServices && !loadingAppointments) && (
          <>
            {/* Calendar view section, rendered conditionally */}
            {isCalendarView && (
              <AppointmentsCalendarView
                calSelectedDate={calSelectedDate}
                setCalSelectedDate={setCalSelectedDate}
                filterDateRange={filterDateRange}
                handleDateRangeChanged={handleDateRangeChanged}
                filteredAppointments={filteredAppointments}
                openEditModal={openEditModal}
                openDeleteConfirmation={openDeleteConfirmation}
                openCancelConfirmation={openCancelConfirmation}
                loadingAppointmentsSmall={loadingAppointmentsSmall}
                getOrgNameFromMap={getOrgNameFromMap}
              />
            )}

            { /* List view section, rendered conditionally */}
            {!isCalendarView && (
              <AppointmentsListView
                filterDateRange={filterDateRange}
                handleDateRangeChanged={handleDateRangeChanged}
                filteredAppointments={filteredAppointments}
                openEditModal={openEditModal}
                openDeleteConfirmation={openDeleteConfirmation}
                openCancelConfirmation={openCancelConfirmation}
                loadingAppointmentsSmall={loadingAppointmentsSmall}
                getOrgNameFromMap={getOrgNameFromMap}
              />
            )}

          </>
        )}
      </div>
    </Dialog>
  )
}
